import React, { useEffect, useState } from "react";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import usePersistedFilters from "../../../Hooks/usePersistedFilters";
import FirmsHeader from "../../../Components/Firm/storageRoles/FirmsHeader";
import FirmsList from "../../../Components/Firm/storageRoles/FirmsList";
import { PageShell } from "../../../Components/shared";
import { useLazyGetFirmQuery } from "../../../store/storageRoles/firmApi";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "../../../Hooks/useDebounce";
import { ALL_FIRM_TAB_PERMISSIONS } from "../../../utils/firmUtils";

function FirmPage() {
  const { auth } = useAuth();
  const { hasAnyPermission } = usePermission();
  const canNavigateToFirm = hasAnyPermission(...ALL_FIRM_TAB_PERMISSIONS);
  const [firms, setFirms] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  // Filtreler sessionStorage'da: firma detayından geri dönünce (browser geri
  // tuşu dahil) aynı arama/filtreyle liste açılır. location.state ile gelen
  // arama (deep-link) storage'ı ezer.
  const [firmFilters, setFirmFilters] = usePersistedFilters(
    "firmList:storage:filters",
    {
      firmName: "",
      mainFirmName: "",
      bayserNo: "",
      dealerName: "",
      firmType: "all",
    },
    location.state?.searchFirmName
      ? { firmName: location.state.searchFirmName }
      : null,
  );
  const {
    firmName: firmNameSearch,
    mainFirmName: mainFirmNameSearch,
    bayserNo: bayserNoSearch,
    dealerName: dealerNameSearch,
    firmType: firmTypeSearch,
  } = firmFilters;
  // Alt bileşen API'si (tekil setter'lar) değişmesin diye alan bazlı sarmalayıcılar
  const makeFilterSetter = (key) => (next) =>
    setFirmFilters((prev) => ({
      ...prev,
      [key]: typeof next === "function" ? next(prev[key]) : next,
    }));
  const setFirmNameSearch = makeFilterSetter("firmName");
  const setMainFirmNameSearch = makeFilterSetter("mainFirmName");
  const setBayserNoSearch = makeFilterSetter("bayserNo");
  const setDealerNameSearch = makeFilterSetter("dealerName");
  const setFirmTypeSearch = makeFilterSetter("firmType");
  const debouncedFirmName = useDebounce(firmNameSearch, 500);
  const debouncedMainFirmName = useDebounce(mainFirmNameSearch, 500);
  const debouncedBayserNo = useDebounce(bayserNoSearch, 500);
  const debouncedDealerName = useDebounce(dealerNameSearch, 500);
  const [
    getFirm,
    { data: firmData, isLoading: firmIsLoading, isFetching: firmIsFetching },
  ] = useLazyGetFirmQuery();

  // Debounce beklerken de loading göster (text input'lar için)
  const isDebouncing =
    firmNameSearch !== debouncedFirmName ||
    mainFirmNameSearch !== debouncedMainFirmName ||
    bayserNoSearch !== debouncedBayserNo ||
    dealerNameSearch !== debouncedDealerName;
  const isLoading = firmIsLoading || firmIsFetching || isDebouncing;

  useEffect(() => {
    if (auth.accessToken) {
      getFirm({
        token: auth.accessToken,
        firmName: debouncedFirmName,
        mainFirmName: debouncedMainFirmName,
        bayserNo: debouncedBayserNo,
        dealerName: debouncedDealerName,
        firmType: firmTypeSearch === "all" ? undefined : firmTypeSearch,
      });
    }
  }, [
    auth.accessToken,
    getFirm,
    debouncedFirmName,
    debouncedMainFirmName,
    debouncedBayserNo,
    debouncedDealerName,
    firmTypeSearch,
  ]);

  useEffect(() => {
    if (firmData?.data?.firms) {
      setFirms(firmData.data.firms);

      // Eğer location state'inde selectedFirmId varsa, direkt route'a yönlendir
      if (location.state?.selectedFirmId) {
        const targetFirm = firmData.data.firms.find(
          (firm) => firm._id === location.state.selectedFirmId
        );
        if (targetFirm) {
          navigate(`/Anasayfa/Firma/${targetFirm._id}`, {
            state: {
              firm: targetFirm,
              fromPage: "Firma",
              toPageTab: location.state?.toPageTab || "info",
            },
            replace: true,
          });
        }
      }
    }
  }, [firmData, location.state?.selectedFirmId, location.state?.toPageTab, navigate]);

  const handleFirmCreated = () => {
    if (auth.accessToken) {
      getFirm({
        token: auth.accessToken,
        firmName: debouncedFirmName,
        mainFirmName: debouncedMainFirmName,
        bayserNo: debouncedBayserNo,
        dealerName: debouncedDealerName,
        firmType: firmTypeSearch === "all" ? undefined : firmTypeSearch,
      });
    }
  };

  return (
    <PageShell>
        <FirmsHeader
          isLoading={isLoading}
          totalFirms={firms.length}
          activeCount={firms.filter((firm) => firm.isActive).length}
          firms={firms}
          setFirms={setFirms}
          onFirmCreated={handleFirmCreated}
        />
        <FirmsList
          firmsData={firms}
          isLoading={isLoading}
          canNavigate={canNavigateToFirm}
          searchParams={{
            firmName: firmNameSearch,
            mainFirmName: mainFirmNameSearch,
            bayserNo: bayserNoSearch,
            dealerName: dealerNameSearch,
            firmType: firmTypeSearch,
          }}
          onSearchParamsChange={{
            setFirmName: setFirmNameSearch,
            setMainFirmName: setMainFirmNameSearch,
            setBayserNo: setBayserNoSearch,
            setDealerName: setDealerNameSearch,
            setFirmType: setFirmTypeSearch,
          }}
        />
    </PageShell>
  );
}

export default FirmPage;
